/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, AnyAction } from "@reduxjs/toolkit";
import { ThunkDispatch } from "redux-thunk";
import _ from "lodash";
import { LoadingState } from "../../Common";
import { User } from "..";
import * as api from "../Api";

export type UserListState = {
  loadingState: LoadingState;
  error?: string;
  users: User[];
  userDetails?: User;
  userDetailsLoadingState: LoadingState;
  userDetailsError?: string;
};

const initialState: UserListState = {
  loadingState: {
    isLoaded: false,
    isLoading: false,
    errored: false
  },
  users: [],
  userDetailsLoadingState: {
    isLoaded: false,
    isLoading: false,
    errored: false
  }
};

const slice = createSlice({
  name: "userList",
  initialState,
  reducers: {
    startLoading: state => {
      state.loadingState = {
        isLoaded: false,
        isLoading: true,
        errored: false
      };
    },
    finishLoading: (state, action: PayloadAction<User[]>) => {
      state.loadingState = {
        isLoaded: true,
        isLoading: false,
        errored: false
      };
      state.users = action.payload;
    },
    errorLoading: (state, action: PayloadAction<string>) => {
      state.loadingState = {
        isLoaded: false,
        isLoading: false,
        errored: true
      };
      state.error = action.payload;
    },
    startLoadingDetails: state => {
      state.userDetailsLoadingState = {
        isLoaded: false,
        isLoading: true,
        errored: false
      };
    },
    finishLoadingDetails: (state, action: PayloadAction<User>) => {
      state.userDetailsLoadingState = {
        isLoaded: true,
        isLoading: false,
        errored: false
      };
      state.userDetails = action.payload;
    },
    errorLoadingDetails: (state, action: PayloadAction<string>) => {
      state.userDetailsLoadingState = {
        isLoaded: false,
        isLoading: false,
        errored: true
      };
      state.userDetailsError = action.payload;
    },
    invalidate: () => initialState
  }
});

export default slice.reducer;

export const { invalidate } = slice.actions;

export const load = () => async (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  dispatch(slice.actions.startLoading());
  try {
    const users = await api.getUsers();
    dispatch(slice.actions.finishLoading(users as User[]));
  } catch (e) {
    dispatch(slice.actions.errorLoading((e as Error).message));
  }
};

export const loadDetails = () => async (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  dispatch(slice.actions.startLoadingDetails());
  try {
    const user = await api.getUserDetails();
    dispatch(slice.actions.finishLoadingDetails(user as User));
  } catch (e) {
    dispatch(slice.actions.errorLoadingDetails((e as Error).message));
  }
};
